import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Edit, Truck, CheckCircle, XCircle, FileText, Download, Trash2, Grid3x3, Loader2, Check } from "lucide-react";

const ZONES = ["N1", "N2", "E", "NE", "W1", "W2", "S1", "S2", "Central"];
const RC_STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700",
  active: "bg-green-100 text-green-700",
  superseded: "bg-slate-100 text-slate-500",
  discarded: "bg-rose-100 text-rose-600",
};
// The overhead fields shown (and editable) in the review panel.
const OVERHEAD_FIELDS = [
  ["fsc_percent", "FSC %"], ["min_charge", "Min LR charge ₹"], ["min_charged_wt", "Min charged wt (kg)"],
  ["volumetric_divisor", "Divisor"], ["docket_charge", "Processing ₹/LR"], ["gst_percent", "GST %"],
  ["oda_per_kg", "ODA ₹/kg"], ["oda_min", "ODA min ₹"],
  ["fm_per_kg", "FM ₹/kg"], ["lm_per_kg", "LM ₹/kg"],
  ["green_tax_per_kg", "Green ₹/kg"], ["green_tax_min", "Green min ₹"],
  ["appointment_per_kg", "Appt ₹/kg"], ["appointment_min", "Appt min ₹"],
  ["rov_percent", "ROV (insurance) %"], ["rov_min", "ROV min ₹"],
  ["demurrage_per_kg", "Demurrage ₹/kg"], ["demurrage_min", "Demurrage min ₹"],
  ["free_storage_days", "Free storage days"], ["floor_delivery_per_kg", "Floor delivery ₹/kg"],
];

// Read-only 9×9 zone-rate grid (origin ↓ × destination →). Used by the persistent
// active-card view; the draft-EDIT grid stays inline in the review panel (inputs).
const ZoneMatrixTable = ({ matrix }) => (
  <div className="overflow-x-auto border rounded-lg">
    <table className="text-xs border-collapse">
      <thead>
        <tr>
          <th className="p-1 sticky left-0 bg-white"></th>
          {ZONES.map((d) => <th key={d} className="p-1 font-semibold text-slate-500">{d}</th>)}
        </tr>
      </thead>
      <tbody>
        {ZONES.map((o) => (
          <tr key={o}>
            <td className="p-1 font-semibold text-slate-500 sticky left-0 bg-white">{o}</td>
            {ZONES.map((d) => {
              const val = matrix?.[o]?.[d];
              return (
                <td key={d} className="p-0.5">
                  <div className={`w-14 px-1 py-0.5 text-right rounded border ${val == null ? "border-red-200 bg-red-50 text-red-400" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
                    {val == null ? "—" : val}
                  </div>
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const B2BShippingPartner = ({ user, onLogout }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    is_aggregator: false,
    api_base_url: "",
    api_username: "",
    api_password: "",
    tracking_interval_minutes: 15,
    lr_copy_fetch_enabled: false,
    lr_copy_fetch_interval_minutes: 30
  });

  // Agreements (per-partner append-only log)
  const emptyAgrForm = { title: "", effective_from: "", signed_date: "", note: "", file: null };
  const [showAgreements, setShowAgreements] = useState(false);
  const [agreementsPartner, setAgreementsPartner] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(false);

  // Rate cards (Gemini-extracted from agreements; draft -> confirm)
  const [showRateCards, setShowRateCards] = useState(false);
  const [rcPartner, setRcPartner] = useState(null);
  const [rateCards, setRateCards] = useState([]);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcSelected, setRcSelected] = useState(null);   // the card being reviewed (local, editable)
  const [rcActive, setRcActive] = useState(null);       // the partner's active card (read-only matrix view)
  const [zoneDef, setZoneDef] = useState([]);           // state→zone definition (reference legend)
  const [rcSaving, setRcSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [agrForm, setAgrForm] = useState(emptyAgrForm);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/b2b-shipping-partner/`);
      setPartners(response.data);
    } catch (error) {
      toast.error("Failed to fetch B2B shipping partners");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePartner = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/b2b-shipping-partner/`, formData);
      toast.success("B2B Shipping Partner created successfully!");
      setShowCreateDialog(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create shipping partner");
    }
  };

  const handleUpdatePartner = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/b2b-shipping-partner/${selectedPartner.id}`, formData);
      toast.success("B2B Shipping Partner updated successfully!");
      setShowEditDialog(false);
      setSelectedPartner(null);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error("Failed to update shipping partner");
    }
  };

  const handleToggleStatus = async (partnerId, currentStatus) => {
    const action = currentStatus ? "disable" : "enable";
    if (!window.confirm(`Are you sure you want to ${action} this shipping partner?`)) return;

    try {
      const response = await axios.patch(`${API}/b2b-shipping-partner/${partnerId}/toggle-status`);
      toast.success(response.data.message);
      fetchPartners();
    } catch (error) {
      toast.error("Failed to update shipping partner status");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      is_aggregator: false,
      api_base_url: "",
      api_username: "",
      api_password: "",
      tracking_interval_minutes: 15,
      lr_copy_fetch_enabled: false,
      lr_copy_fetch_interval_minutes: 30
    });
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setFormData({
      name: partner.name,
      logo_url: partner.logo_url || "",
      is_aggregator: partner.is_aggregator || false,
      api_base_url: partner.api_base_url || "",
      api_username: partner.api_username || "",
      api_password: partner.api_password || "",
      tracking_interval_minutes: partner.tracking_interval_minutes || 15,
      lr_copy_fetch_enabled: partner.lr_copy_fetch_enabled || false,
      lr_copy_fetch_interval_minutes: partner.lr_copy_fetch_interval_minutes || 30
    });
    setShowEditDialog(true);
  };

  const openAgreements = (partner) => {
    setAgreementsPartner(partner);
    setAgrForm(emptyAgrForm);
    setAgreements([]);
    setShowAgreements(true);
    fetchAgreements(partner.id);
  };

  const fetchAgreements = async (partnerId) => {
    try {
      setAgreementsLoading(true);
      const res = await axios.get(`${API}/b2b-shipping-partner/${partnerId}/agreements`);
      setAgreements(res.data.agreements || []);
    } catch (error) {
      toast.error("Failed to load agreements");
    } finally {
      setAgreementsLoading(false);
    }
  };

  const handleUploadAgreement = async (e) => {
    e.preventDefault();
    if (!agreementsPartner) return;
    if (!agrForm.file) { toast.error("Please choose a PDF file"); return; }
    if (!agrForm.title.trim()) { toast.error("Please enter a title"); return; }
    if (!agrForm.effective_from) { toast.error("Please set the effective-from date"); return; }
    const fd = new FormData();
    fd.append("file", agrForm.file);
    fd.append("title", agrForm.title.trim());
    fd.append("effective_from", agrForm.effective_from);
    if (agrForm.signed_date) fd.append("signed_date", agrForm.signed_date);
    if (agrForm.note.trim()) fd.append("note", agrForm.note.trim());
    try {
      setUploading(true);
      await axios.post(`${API}/b2b-shipping-partner/${agreementsPartner.id}/agreements`, fd);
      toast.success("Agreement uploaded");
      setAgrForm(emptyAgrForm);
      fetchAgreements(agreementsPartner.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload agreement");
    } finally {
      setUploading(false);
    }
  };

  const handleVoidAgreement = async (agreementId) => {
    const reason = window.prompt("Void this agreement entry — reason (it stays in the log, marked voided):");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    const fd = new FormData();
    fd.append("reason", reason.trim());
    try {
      await axios.post(`${API}/b2b-shipping-partner/${agreementsPartner.id}/agreements/${agreementId}/void`, fd);
      toast.success("Agreement voided");
      fetchAgreements(agreementsPartner.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to void agreement");
    }
  };

  // ── Rate cards ──
  const openRateCards = (partner) => {
    setRcPartner(partner);
    setRateCards([]);
    setRcActive(null);
    setRcSelected(null);
    setShowRateCards(true);
    fetchRateCards(partner.id);
    if (zoneDef.length === 0) {
      axios.get(`${API}/rate-validation/zone-definition`)
        .then((r) => setZoneDef(r.data.zones || []))
        .catch(() => {});   // legend is best-effort; the matrix still shows without it
    }
  };
  const fetchRateCards = async (partnerId) => {
    try {
      setRcLoading(true);
      const res = await axios.get(`${API}/partner-rate-cards`, { params: { partner_id: partnerId } });
      setRateCards(res.data.cards || []);
      setRcActive(res.data.active || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load rate cards");
    } finally {
      setRcLoading(false);
    }
  };
  const reviewCard = (card) => setRcSelected(JSON.parse(JSON.stringify(card)));  // editable local copy
  const setCell = (o, d, val) => {
    const v = val === "" ? null : Number(val);
    setRcSelected((c) => {
      const zm = { ...(c.zone_matrix || {}) };
      zm[o] = { ...(zm[o] || {}), [d]: v };
      return { ...c, zone_matrix: zm };
    });
  };
  const setOverhead = (key, val) => {
    const v = val === "" ? null : Number(val);
    setRcSelected((c) => ({ ...c, overheads: { ...(c.overheads || {}), [key]: v } }));
  };
  const saveDraft = async () => {
    setRcSaving(true);
    try {
      const body = { zone_matrix: rcSelected.zone_matrix, overheads: rcSelected.overheads,
                     title: rcSelected.title, effective_from: rcSelected.effective_from };
      const res = await axios.put(`${API}/partner-rate-cards/${rcSelected.id}`, body);
      toast.success("Draft saved");
      setRcSelected(res.data);
      fetchRateCards(rcPartner.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save draft");
    } finally {
      setRcSaving(false);
    }
  };
  const confirmCard = async (card) => {
    if (!window.confirm(`Activate this rate card (effective ${card.effective_from})? It becomes the card used for all freight validation and supersedes the current active one.`)) return;
    setRcSaving(true);
    try {
      await axios.post(`${API}/partner-rate-cards/${card.id}/confirm`);
      toast.success("Rate card activated");
      setRcSelected(null);
      fetchRateCards(rcPartner.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm");
    } finally {
      setRcSaving(false);
    }
  };
  const discardCard = async (card) => {
    if (!window.confirm("Discard this draft? It stays in the log, marked discarded.")) return;
    try {
      await axios.post(`${API}/partner-rate-cards/${card.id}/discard`);
      toast.success("Draft discarded");
      setRcSelected(null);
      fetchRateCards(rcPartner.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to discard");
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">B2B Shipping Partner</h1>
            <p className="text-slate-600">Manage B2B shipping partner integrations</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{backgroundColor: '#00A699'}}>
                <Plus className="w-4 h-4 mr-2" />
                Add New
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add B2B Shipping Partner</DialogTitle>
                <DialogDescription>Create a new B2B shipping partner entry</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePartner} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo_url">Logo URL</Label>
                  <Input
                    id="logo_url"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://example.com/logo.png"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_aggregator"
                      checked={formData.is_aggregator}
                      onChange={(e) => setFormData({ ...formData, is_aggregator: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]"
                    />
                    <Label htmlFor="is_aggregator" className="font-normal cursor-pointer">
                      Is Shipping Aggregator
                      <span className="text-xs text-slate-500 ml-2">(Check if this partner aggregates multiple shipping services)</span>
                    </Label>
                  </div>
                </div>

                {/* API Configuration for Auto LR Assignment */}
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium text-slate-900">API Configuration (Optional)</h4>
                  <p className="text-xs text-slate-500">Configure API credentials to enable automatic LR number generation</p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="api_base_url">API Base URL</Label>
                    <Input
                      id="api_base_url"
                      value={formData.api_base_url}
                      onChange={(e) => setFormData({ ...formData, api_base_url: e.target.value })}
                      placeholder="https://api.example.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="api_username">API Username</Label>
                    <Input
                      id="api_username"
                      value={formData.api_username}
                      onChange={(e) => setFormData({ ...formData, api_username: e.target.value })}
                      placeholder="Enter API username"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="api_password">API Password</Label>
                    <Input
                      id="api_password"
                      type="password"
                      value={formData.api_password}
                      onChange={(e) => setFormData({ ...formData, api_password: e.target.value })}
                      placeholder="Enter API password"
                    />
                  </div>
                </div>

                {/* Tracking Configuration */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-slate-700">Tracking Configuration</h3>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tracking_interval">Tracking Interval (minutes)</Label>
                    <Input
                      id="tracking_interval"
                      type="number"
                      min="5"
                      max="1440"
                      value={formData.tracking_interval_minutes}
                      onChange={(e) => setFormData({ ...formData, tracking_interval_minutes: parseInt(e.target.value) || 15 })}
                      placeholder="15"
                    />
                    <p className="text-xs text-slate-500">
                      How often to track LRN status (5-1440 minutes). Default: 15 minutes
                    </p>
                  </div>
                </div>

                {/* LR Copy Fetch Configuration */}
                <div className="border-t pt-4 space-y-4">
                  <h4 className="font-medium text-slate-900">LR Copy Auto-Fetch</h4>
                  <p className="text-xs text-slate-500">Automatically fetch and store LR copy documents for orders</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="lr_copy_fetch_enabled"
                      checked={formData.lr_copy_fetch_enabled}
                      onChange={(e) => setFormData({ ...formData, lr_copy_fetch_enabled: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]"
                      data-testid="lr-copy-fetch-enabled-create"
                    />
                    <Label htmlFor="lr_copy_fetch_enabled" className="font-normal cursor-pointer">
                      Enable automatic LR copy fetching
                    </Label>
                  </div>
                  {formData.lr_copy_fetch_enabled && (
                    <div className="space-y-2">
                      <Label htmlFor="lr_copy_fetch_interval">Fetch Interval (minutes)</Label>
                      <Input
                        id="lr_copy_fetch_interval"
                        type="number"
                        min="5"
                        max="1440"
                        value={formData.lr_copy_fetch_interval_minutes}
                        onChange={(e) => setFormData({ ...formData, lr_copy_fetch_interval_minutes: parseInt(e.target.value) || 30 })}
                        placeholder="30"
                        data-testid="lr-copy-fetch-interval-create"
                      />
                      <p className="text-xs text-slate-500">
                        How often to retry fetching LR copies per order (5-1440 minutes). Default: 30 minutes
                      </p>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
                  Create Shipping Partner
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex space-x-4">
                  <div className="h-12 bg-slate-200 rounded w-full"></div>
                </div>
              ))}
            </div>
          </div>
        ) : partners.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Truck className="w-16 h-16 text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No B2B shipping partners yet</h3>
              <p className="text-slate-500 mb-6">Add your first shipping partner to get started</p>
              <Button style={{backgroundColor: '#00A699'}} onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Shipping Partner
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Logo</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">API Integration</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {partners.map((partner) => (
                    <tr key={partner.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {partner.logo_url ? (
                          <img src={partner.logo_url} alt={partner.name} className="h-10 w-10 object-contain" />
                        ) : (
                          <Truck className="h-10 w-10 text-slate-300" />
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-sm font-medium text-slate-900">{partner.name}</span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {partner.is_aggregator ? (
                          <Badge className="bg-blue-100 text-blue-800 border-0">
                            <Truck className="w-3 h-3 mr-1" />
                            Aggregator
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-700 border-0">
                            Direct
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        {partner.api_base_url && partner.api_username ? (
                          <Badge className="bg-green-100 text-green-800 border-0">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500 border-0">
                            <XCircle className="w-3 h-3 mr-1" />
                            Not Configured
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <Badge className={partner.is_active ? "bg-green-100 text-green-800 border-0" : "bg-red-100 text-red-800 border-0"}>
                          {partner.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(partner)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openAgreements(partner)}
                            title="Agreements"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openRateCards(partner)}
                            title="Rate Cards"
                            data-testid="open-rate-cards"
                          >
                            <Grid3x3 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className={partner.is_active ? "text-orange-600 hover:text-orange-700" : "text-green-600 hover:text-green-700"}
                            onClick={() => handleToggleStatus(partner.id, partner.is_active)}
                            title={partner.is_active ? "Disable" : "Enable"}
                          >
                            {partner.is_active ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit B2B Shipping Partner</DialogTitle>
            <DialogDescription>Update shipping partner details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePartner} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Name *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_logo_url">Logo URL</Label>
              <Input
                id="edit_logo_url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit_is_aggregator"
                  checked={formData.is_aggregator}
                  onChange={(e) => setFormData({ ...formData, is_aggregator: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]"
                />
                <Label htmlFor="edit_is_aggregator" className="font-normal cursor-pointer">
                  Is Shipping Aggregator
                  <span className="text-xs text-slate-500 ml-2">(Check if this partner aggregates multiple shipping services)</span>
                </Label>
              </div>
            </div>

            {/* API Configuration for Auto LR Assignment */}
            <div className="border-t pt-4 space-y-4">
              <h4 className="font-medium text-slate-900">API Configuration (Optional)</h4>
              <p className="text-xs text-slate-500">Configure API credentials to enable automatic LR number generation</p>
              
              <div className="space-y-2">
                <Label htmlFor="edit_api_base_url">API Base URL</Label>
                <Input
                  id="edit_api_base_url"
                  value={formData.api_base_url}
                  onChange={(e) => setFormData({ ...formData, api_base_url: e.target.value })}
                  placeholder="https://api.example.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_api_username">API Username</Label>
                <Input
                  id="edit_api_username"
                  value={formData.api_username}
                  onChange={(e) => setFormData({ ...formData, api_username: e.target.value })}
                  placeholder="Enter API username"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit_api_password">API Password</Label>
                <Input
                  id="edit_api_password"
                  type="password"
                  value={formData.api_password}
                  onChange={(e) => setFormData({ ...formData, api_password: e.target.value })}
                  placeholder="Leave blank to keep existing password"
                />
                <p className="text-xs text-slate-500">Leave blank to keep the existing password</p>
              </div>

              {/* Tracking Configuration */}
              <div className="space-y-2">
                <Label htmlFor="edit_tracking_interval">Tracking Interval (minutes)</Label>
                <Input
                  id="edit_tracking_interval"
                  type="number"
                  min="5"
                  max="1440"
                  value={formData.tracking_interval_minutes}
                  onChange={(e) => setFormData({ ...formData, tracking_interval_minutes: parseInt(e.target.value) || 15 })}
                  placeholder="15"
                />
                <p className="text-xs text-slate-500">
                  How often to track LRN status (5-1440 minutes). Default: 15 minutes
                </p>
              </div>

              {/* LR Copy Fetch Configuration */}
              <div className="border-t pt-4 space-y-4">
                <h4 className="font-medium text-slate-900">LR Copy Auto-Fetch</h4>
                <p className="text-xs text-slate-500">Automatically fetch and store LR copy documents for orders</p>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_lr_copy_fetch_enabled"
                    checked={formData.lr_copy_fetch_enabled}
                    onChange={(e) => setFormData({ ...formData, lr_copy_fetch_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-[#00A699] focus:ring-[#00A699]"
                    data-testid="lr-copy-fetch-enabled-edit"
                  />
                  <Label htmlFor="edit_lr_copy_fetch_enabled" className="font-normal cursor-pointer">
                    Enable automatic LR copy fetching
                  </Label>
                </div>
                {formData.lr_copy_fetch_enabled && (
                  <div className="space-y-2">
                    <Label htmlFor="edit_lr_copy_fetch_interval">Fetch Interval (minutes)</Label>
                    <Input
                      id="edit_lr_copy_fetch_interval"
                      type="number"
                      min="5"
                      max="1440"
                      value={formData.lr_copy_fetch_interval_minutes}
                      onChange={(e) => setFormData({ ...formData, lr_copy_fetch_interval_minutes: parseInt(e.target.value) || 30 })}
                      placeholder="30"
                      data-testid="lr-copy-fetch-interval-edit"
                    />
                    <p className="text-xs text-slate-500">
                      How often to retry fetching LR copies per order (5-1440 minutes). Default: 30 minutes
                    </p>
                  </div>
                )}
              </div>
            </div>

            <Button type="submit" className="w-full" style={{backgroundColor: '#00A699'}}>
              Update Shipping Partner
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Agreements Dialog — append-only signed-agreement log per partner */}
      <Dialog open={showAgreements} onOpenChange={setShowAgreements}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agreements — {agreementsPartner?.name}</DialogTitle>
            <DialogDescription>
              Signed partner agreements. Each upload is appended to a permanent log (never overwritten).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUploadAgreement} className="space-y-3 border border-slate-200 rounded-lg p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1 sm:col-span-2">
                <Label>Title *</Label>
                <Input value={agrForm.title} onChange={(e) => setAgrForm({ ...agrForm, title: e.target.value })}
                  placeholder="e.g. Addendum 6 — Rate CFT 6" />
              </div>
              <div className="space-y-1">
                <Label>Effective from *</Label>
                <Input type="date" value={agrForm.effective_from}
                  onChange={(e) => setAgrForm({ ...agrForm, effective_from: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Signed date</Label>
                <Input type="date" value={agrForm.signed_date}
                  onChange={(e) => setAgrForm({ ...agrForm, signed_date: e.target.value })} />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label>Note</Label>
                <Textarea value={agrForm.note} rows={2}
                  onChange={(e) => setAgrForm({ ...agrForm, note: e.target.value })}
                  placeholder="Optional note" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label>Agreement PDF *</Label>
                <Input type="file" accept="application/pdf"
                  onChange={(e) => setAgrForm({ ...agrForm, file: e.target.files?.[0] || null })} />
              </div>
            </div>
            <Button type="submit" disabled={uploading} style={{ backgroundColor: "#00A699" }}>
              {uploading ? "Uploading…" : "Upload Agreement"}
            </Button>
          </form>

          <div className="mt-2">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Agreement log</h4>
            {agreementsLoading ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : agreements.length === 0 ? (
              <p className="text-sm text-slate-500">No agreements uploaded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-slate-500 border-b">
                      <th className="py-2 pr-3">Title</th>
                      <th className="py-2 pr-3">Effective from</th>
                      <th className="py-2 pr-3">Signed</th>
                      <th className="py-2 pr-3">Uploaded by</th>
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {agreements.map((a) => (
                      <tr key={a.id} className={`border-b ${a.voided_at ? "opacity-50" : ""}`}>
                        <td className="py-2 pr-3">
                          {a.title}
                          {a.voided_at && <Badge className="ml-2 bg-red-100 text-red-700 border-0">Voided</Badge>}
                          {a.note && <div className="text-xs text-slate-400">{a.note}</div>}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">{a.effective_from || "—"}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{a.signed_date || "—"}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{a.uploaded_by?.user_name || a.uploaded_by?.user_email || "—"}</td>
                        <td className="py-2 pr-3 whitespace-nowrap text-xs text-slate-500">
                          {a.uploaded_at ? new Date(a.uploaded_at).toLocaleString() : "—"}
                        </td>
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <div className="flex gap-1">
                            {a.download_url && (
                              <a href={a.download_url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center text-teal-600 hover:text-teal-700" title="Download">
                                <Download className="w-4 h-4" />
                              </a>
                            )}
                            {!a.voided_at && (
                              <button type="button" onClick={() => handleVoidAgreement(a.id)}
                                className="inline-flex items-center text-red-500 hover:text-red-700" title="Void">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Rate Cards — Gemini-extracted from the signed agreement; review & confirm */}
      <Dialog open={showRateCards} onOpenChange={(o) => { setShowRateCards(o); if (!o) setRcSelected(null); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rate Cards — {rcPartner?.name}</DialogTitle>
            <DialogDescription>
              Extracted from the signed agreement on upload. Review a draft, fix any
              flagged cell, then Confirm to make it the active card used for freight validation.
            </DialogDescription>
          </DialogHeader>

          {!rcSelected ? (
            <div className="space-y-5">
              {/* Active card — zone matrix (₹/kg) + zone definition, read-only reference */}
              {rcActive && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">Active zone matrix — ₹/kg (origin ↓ × destination →)</div>
                    <Badge className={RC_STATUS_COLORS.active}>active · {rcActive.effective_from}</Badge>
                  </div>
                  <ZoneMatrixTable matrix={rcActive.zone_matrix} />
                  {zoneDef.length > 0 && (
                    <div>
                      <div className="text-sm font-medium mt-2 mb-1">Zone definition — states in each zone</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {zoneDef.map((z) => (
                          <div key={z.zone} className="border rounded-lg p-2">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-[#00A699] bg-opacity-10 text-[#00A699] border border-[#00A699]">{z.zone}</Badge>
                              <span className="text-xs font-medium text-slate-600">{z.label}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {z.states.map((s) => <Badge key={s} variant="outline" className="text-[10px] font-normal">{s}</Badge>)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* ── all versions ── */}
              {rcLoading ? (
              <div className="py-8 text-center text-slate-400"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
            ) : rateCards.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500">
                No rate cards yet. Upload a signed agreement (Agreements) — a draft card is extracted automatically.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 border-b">
                    <tr>
                      <th className="text-left py-2 px-2">Effective</th>
                      <th className="text-left py-2 px-2">Title</th>
                      <th className="text-left py-2 px-2">Status</th>
                      <th className="text-right py-2 px-2">Confidence</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateCards.map((c) => (
                      <tr key={c.id} className="border-b">
                        <td className="py-2 px-2 font-medium">{c.effective_from}</td>
                        <td className="py-2 px-2">{c.title}</td>
                        <td className="py-2 px-2"><Badge className={RC_STATUS_COLORS[c.status] || "bg-slate-100"}>{c.status}</Badge></td>
                        <td className="py-2 px-2 text-right">{c.parse_confidence != null ? `${Math.round(c.parse_confidence * 100)}%` : "—"}</td>
                        <td className="py-2 px-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => reviewCard(c)}>Review</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            </div>
          ) : (
            // ── review panel ──
            (() => {
              const isDraft = rcSelected.status === "draft";
              const conf = rcSelected.parse_confidence;
              const nullCells = ZONES.reduce((n, o) => n + ZONES.filter((d) => (rcSelected.zone_matrix?.[o]?.[d] == null)).length, 0);
              return (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <button className="text-sm text-[#00A699] hover:underline" onClick={() => setRcSelected(null)}>← All versions</button>
                    <Badge className={RC_STATUS_COLORS[rcSelected.status]}>{rcSelected.status}</Badge>
                  </div>

                  {/* confidence banner */}
                  <div className={`rounded-lg p-3 text-sm ${conf == null ? "bg-slate-50" : conf >= 0.9 ? "bg-green-50 text-green-800" : conf >= 0.7 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800"}`}>
                    Extraction confidence: <b>{conf != null ? `${Math.round(conf * 100)}%` : "n/a"}</b>
                    {nullCells > 0 && <span> · {nullCells} matrix cell(s) couldn’t be read — fill the red cells before confirming.</span>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Title</Label>
                      <Input value={rcSelected.title || ""} disabled={!isDraft} onChange={(e) => setRcSelected((c) => ({ ...c, title: e.target.value }))} /></div>
                    <div><Label className="text-xs">Effective from</Label>
                      <Input type="date" value={rcSelected.effective_from || ""} disabled={!isDraft} onChange={(e) => setRcSelected((c) => ({ ...c, effective_from: e.target.value }))} /></div>
                  </div>

                  {/* 9x9 zone matrix (origin rows x dest cols), ₹/kg */}
                  <div>
                    <div className="text-sm font-medium mb-1">Zone matrix — ₹/kg (origin ↓ × destination →)</div>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="text-xs border-collapse">
                        <thead>
                          <tr>
                            <th className="p-1 sticky left-0 bg-white"></th>
                            {ZONES.map((d) => <th key={d} className="p-1 font-semibold text-slate-500">{d}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {ZONES.map((o) => (
                            <tr key={o}>
                              <td className="p-1 font-semibold text-slate-500 sticky left-0 bg-white">{o}</td>
                              {ZONES.map((d) => {
                                const val = rcSelected.zone_matrix?.[o]?.[d];
                                return (
                                  <td key={d} className="p-0.5">
                                    <input type="number" step="0.1" value={val ?? ""} disabled={!isDraft}
                                      onChange={(e) => setCell(o, d, e.target.value)}
                                      className={`w-14 px-1 py-0.5 text-right rounded border ${val == null ? "border-red-300 bg-red-50" : "border-slate-200"} ${isDraft ? "" : "bg-slate-50 text-slate-500"}`} />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* overheads */}
                  <div>
                    <div className="text-sm font-medium mb-1">Overheads</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {OVERHEAD_FIELDS.map(([key, label]) => (
                        <div key={key}>
                          <Label className="text-[11px] text-slate-500">{label}</Label>
                          <Input type="number" step="0.01" value={rcSelected.overheads?.[key] ?? ""} disabled={!isDraft}
                            onChange={(e) => setOverhead(key, e.target.value)} className="h-8" />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* lane overrides — destination state/city rate that beats the matrix (validator uses these) */}
                  {(() => {
                    const byState = rcSelected.lane_overrides_by_state || {};
                    const byCity = rcSelected.lane_overrides_by_city || {};
                    const rows = [
                      ...Object.entries(byState).map(([name, rates]) => ({ name, kind: "state", rates })),
                      ...Object.entries(byCity).map(([name, rates]) => ({ name, kind: "city", rates })),
                    ];
                    if (rows.length === 0) return null;
                    return (
                      <div>
                        <div className="text-sm font-medium mb-1">Lane overrides — ₹/kg by origin zone (destination beats the matrix)</div>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="text-xs border-collapse">
                            <thead>
                              <tr>
                                <th className="p-1 text-left sticky left-0 bg-white">Destination</th>
                                {ZONES.map((z) => <th key={z} className="p-1 font-semibold text-slate-500">{z}</th>)}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row) => (
                                <tr key={row.kind + row.name}>
                                  <td className="p-1 font-medium text-slate-600 sticky left-0 bg-white whitespace-nowrap capitalize">{row.name}{row.kind === "city" ? " (city)" : ""}</td>
                                  {ZONES.map((z) => {
                                    const v = row.rates?.[z];
                                    return <td key={z} className="p-1 text-right text-slate-700">{v == null ? "—" : v}</td>;
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}

                  {/* handling slabs */}
                  {Array.isArray(rcSelected.overheads?.handling_slabs) && rcSelected.overheads.handling_slabs.length > 0 && (
                    <div>
                      <div className="text-sm font-medium mb-1">Handling slabs (₹/kg by weight)</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {rcSelected.overheads.handling_slabs.map((s, i) => (
                          <span key={i} className="rounded border px-2 py-1 bg-slate-50">
                            {s.from_kg ?? 0}–{s.to_kg ?? "∞"} kg: ₹{s.per_kg ?? 0}/kg
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* fuel config */}
                  {rcSelected.extra?.fuel && Object.values(rcSelected.extra.fuel).some((v) => v != null) && (
                    <div>
                      <div className="text-sm font-medium mb-1">Fuel</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {[["base_rate_per_litre", "Base rate ₹/L"], ["hike_threshold", "Hike threshold"],
                          ["hike_duration", "Hike duration"], ["change_in_fuel_rate", "Δ fuel rate"],
                          ["change_in_freight_pct", "Δ freight %"], ["hike_application", "Applies to"]]
                          .filter(([k]) => rcSelected.extra.fuel[k] != null)
                          .map(([k, l]) => (
                            <span key={k} className="rounded border px-2 py-1 bg-slate-50">
                              <span className="text-slate-500">{l}:</span> {rcSelected.extra.fuel[k]}
                            </span>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* other agreement charges */}
                  {Array.isArray(rcSelected.extra?.extra_charges) && rcSelected.extra.extra_charges.length > 0 && (
                    <div>
                      <div className="text-sm font-medium mb-1">Other agreement charges</div>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500">
                            <tr><th className="text-left px-2 py-1">Charge</th><th className="px-2 py-1">Per</th>
                              <th className="text-right px-2 py-1">Rate</th><th className="text-right px-2 py-1">Min</th>
                              <th className="text-right px-2 py-1">Max</th></tr>
                          </thead>
                          <tbody>
                            {rcSelected.extra.extra_charges.map((c, i) => (
                              <tr key={i} className="border-t">
                                <td className="px-2 py-1">{c.name}</td>
                                <td className="px-2 py-1 text-center">{c.per || "—"}</td>
                                <td className="px-2 py-1 text-right">{c.rate ?? "—"}</td>
                                <td className="px-2 py-1 text-right">{c.min ?? "—"}</td>
                                <td className="px-2 py-1 text-right">{c.max ?? "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* billing */}
                  {rcSelected.extra?.billing && Object.values(rcSelected.extra.billing).some((v) => v != null) && (
                    <div className="text-xs text-slate-600">
                      <span className="font-medium">Billing:</span>{" "}
                      {rcSelected.extra.billing.invoice_type || "—"} · {rcSelected.extra.billing.cycle || "—"} · start day {rcSelected.extra.billing.start_date ?? "—"}
                    </div>
                  )}

                  {/* other agreement parameters (catch-all, from a Gemini extraction) */}
                  {Array.isArray(rcSelected.extra?.other_parameters) && rcSelected.extra.other_parameters.length > 0 && (
                    <div>
                      <div className="text-sm font-medium mb-1">Other agreement parameters</div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {rcSelected.extra.other_parameters.map((p, i) => (
                          <span key={i} className="rounded border px-2 py-1 bg-slate-50">
                            <span className="text-slate-500">{p.name}:</span> {p.value}{p.unit ? ` ${p.unit}` : ""}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t">
                    {isDraft && <Button variant="outline" onClick={() => discardCard(rcSelected)}><Trash2 className="w-4 h-4 mr-1" /> Discard</Button>}
                    {isDraft && <Button variant="outline" onClick={saveDraft} disabled={rcSaving}>{rcSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Save draft</Button>}
                    {isDraft && <Button onClick={() => confirmCard(rcSelected)} disabled={rcSaving} style={{ backgroundColor: "#00A699" }} className="hover:opacity-90"><Check className="w-4 h-4 mr-1" /> Confirm &amp; activate</Button>}
                    {!isDraft && <span className="text-xs text-slate-400">{rcSelected.status === "active" ? "This is the active card (read-only)." : "Read-only."}</span>}
                  </div>
                </div>
              );
            })()
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default B2BShippingPartner;